import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';

interface PaymentQuickLink {
  label: string;
  route: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-payment-services-payments-page',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Payments</h1>
          <p class="page-sub">Select a payment operation for purchase, authorization, verification, and post-transaction handling.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="quick-grid">
            @for (link of quickLinks; track link.route) {
              <a class="quick-card" [routerLink]="link.route">
                <div class="quick-card-hd">
                  <span class="quick-icon">{{ link.icon }}</span>
                  <h3>{{ link.label }}</h3>
                </div>
                <p>{{ link.description }}</p>
              </a>
            }
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.quick-grid{display:grid;gap:1rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);padding:1rem 1.1rem;text-decoration:none;color:var(--color-text);transition:transform .15s ease, border-color .15s ease}.quick-card:hover{transform:translateY(-2px);border-color:color-mix(in srgb,var(--color-accent) 38%,var(--color-border))}.quick-card-hd{display:flex;align-items:center;gap:.5rem;margin-bottom:.4rem}.quick-icon{display:inline-flex;align-items:center;justify-content:center;width:1.2rem;height:1.2rem}.quick-card h3{margin:0;font-size:1rem}.quick-card p{margin:0;color:var(--color-muted);font-size:.88rem}`]
})
export class PaymentServicesPaymentsPageComponent {
  readonly quickLinks: PaymentQuickLink[] = [
    { label: 'Purchase', route: '/payment-services/payments/purchase', description: 'Launch HelcimPay modal checkout and complete purchase.', icon: '🛒' },
    { label: 'PreAuth', route: '/payment-services/payments/preauth', description: 'Authorize card funds before capture.', icon: '✅' },
    { label: 'Capture', route: '/payment-services/payments/capture', description: 'Capture previously authorized card transactions.', icon: '📥' },
    { label: 'Verify', route: '/payment-services/payments/verify', description: 'Run card verification with billing checks.', icon: '🔎' },
    { label: 'Refund', route: '/payment-services/payments/refund', description: 'Refund completed payment transactions.', icon: '↩️' },
    { label: 'Reversal', route: '/payment-services/payments/reversal', description: 'Reverse unsettled payment transactions.', icon: '🔁' }
  ];
}
